import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { detectBrowserLanguage } from '@/utils/browserLanguageDetector';
import { extractLanguageFromPath, getCanonicalPath } from '@/utils/routing/multilingualRouting';
import { Language } from '@/context/languages/languageData';

/**
 * Component that manages automatic language redirects and canonical URL enforcement
 */
export const LanguageRedirectManager: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    const currentPath = location.pathname;
    const urlLanguage = extractLanguageFromPath(currentPath);
    const canonicalPath = getCanonicalPath(currentPath);

    // Skip redirect logic for development routes
    if (currentPath.startsWith('/dev/')) {
      return;
    }

    // If no language in URL and user has a preferred language (not English)
    if (!urlLanguage && language !== 'en') {
      const newPath = `/${language}${canonicalPath === '/' ? '' : canonicalPath}`;
      console.log(`Redirecting to language-specific path: ${newPath}`);
      navigate(newPath, { replace: true });
      return;
    }

    // If URL language doesn't match context language, update context
    if (urlLanguage && urlLanguage !== language) {
      console.log(`URL language (${urlLanguage}) differs from context (${language})`);
      // The LanguageContext will handle this automatically
    }

    // Handle root path special cases
    if (currentPath === '/' && language !== 'en') {
      const browserLang = detectBrowserLanguage();
      if (browserLang !== 'en' && browserLang === language) {
        console.log(`Considering redirect to ${browserLang} for root path`);
        // For now, we keep root path as English for SEO
        // but this could be customized based on business needs
      }
    }

  }, [location.pathname, language, navigate]);

  // This component doesn't render anything
  return null;
};

/**
 * Hook for programmatic language redirects
 */
export const useLanguageRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const redirectToLanguage = (targetLanguage: Language, preservePath = true) => {
    const currentPath = location.pathname;
    const canonicalPath = preservePath ? getCanonicalPath(currentPath) : '/';
    
    const newPath = targetLanguage === 'en' 
      ? canonicalPath 
      : `/${targetLanguage}${canonicalPath === '/' ? '' : canonicalPath}`;
    
    navigate(newPath, { replace: true });
  };

  const redirectToCanonical = () => {
    const currentPath = location.pathname;
    const canonicalPath = getCanonicalPath(currentPath);
    
    if (currentPath !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  };

  return {
    redirectToLanguage,
    redirectToCanonical
  };
};

export default LanguageRedirectManager;
